import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { LuLink, LuUpload } from "react-icons/lu";
import { Confirm } from "react-admin";
import ProviderToggle from "./ProviderToggle";
import DeleteContainedBtn from "../../common/DeleteContainedBtn";
import ArgylePayrollValidationMessages from "./ArgylePayrollValidationMessages";

type EmployerVerificationChooserProps = {
  employerIndex: number;
  employerName?: string;
  trn_id: string;
  sourceId: number;
  validationMessage?: string;
  onSelectDocumentUpload: (options?: { precheckOfferLetter?: boolean }) => void;
  onPayrollLinked?: () => void;
  onDeleteSource?: () => void;
  deleting?: boolean;
  payrollPending?: boolean;
  payrollPendingStatus?: "pending" | "linked_no_data" | "linked" | "valid" | "none";
  showArgyleValidationMessages?: boolean;
  argyleValidationMessages?: Record<string, any[]>;
  role?: string;
};

const EmployerVerificationChooser = ({
  employerIndex,
  employerName,
  trn_id,
  sourceId,
  validationMessage,
  onSelectDocumentUpload,
  onPayrollLinked,
  onDeleteSource,
  deleting = false,
  payrollPending = false,
  payrollPendingStatus = "none",
  showArgyleValidationMessages = false,
  argyleValidationMessages,
  role,
}: EmployerVerificationChooserProps) => {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [payrollLinkActionLocked, setPayrollLinkActionLocked] = useState(false);
  const chooserButtonWidth = 256;

  useEffect(() => {
    if (payrollPending) {
      setPayrollLinkActionLocked(false);
    }
  }, [payrollPending]);

  const chooserActionsDisabled = deleting || payrollPending || payrollLinkActionLocked;
  const pendingMessage =
    payrollPendingStatus === "linked_no_data"
      ? "Payroll account is linked, but no pay data was found in the configured lookback period yet."
      : "Payroll is connecting. You can continue while we load your payroll data.";

  return (
    <div className="pt-1">
      <div className="login-radio-types p-2">
        <Card
          sx={{
            background: "#f5fdff",
            boxShadow: "none",
            borderBottom: "1px solid black",
            borderRadius: 0,
          }}
        >
          <CardHeader
            sx={{ padding: 0 }}
            title={
              <b style={{ fontSize: 13 }}>{`Employer ${employerIndex + 1}:${
                employerName ? ` ${employerName}` : ""
              }`}</b>
            }
            action={
              onDeleteSource ? (
                <DeleteContainedBtn
                  disable={chooserActionsDisabled}
                  onClick={() => setConfirmDeleteOpen(true)}
                />
              ) : null
            }
          />
        </Card>

        <CardContent sx={{ px: 0 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            How will you verify income for this employer?
          </Typography>
          {payrollPending ? (
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <CircularProgress size="20px" />
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 0 }}>
                {pendingMessage}
              </Typography>
            </div>
          ) : (
            <div className="d-flex flex-column" style={{ gap: 8 }}>
              <div className="d-flex flex-wrap align-items-center" style={{ gap: 12 }}>
                <button
                  type="button"
                  className="employment-chooser-action-btn"
                  onClick={() => onSelectDocumentUpload()}
                  disabled={chooserActionsDisabled}
                  style={{ width: chooserButtonWidth }}
                >
                <LuUpload size={17} />
                Upload Pay Stub PDFs
              </button>
              <ProviderToggle
                trn_id={trn_id}
                sourceId={sourceId}
                triggerLabel="Connect Payroll"
                linkType="payroll"
                onProviderSuccess={onPayrollLinked}
                disabled={chooserActionsDisabled}
                onFlowStarted={() => setPayrollLinkActionLocked(true)}
                onFlowExited={(completed) => {
                  if (!completed) {
                    setPayrollLinkActionLocked(false);
                  }
                }}
                includeRelinkWarning
                triggerIcon={<LuLink size={17} />}
                triggerContainerClassName="d-flex align-items-center"
                triggerContainerStyle={{ width: chooserButtonWidth }}
                triggerRowClassName="d-flex align-items-center"
                triggerRowStyle={{ width: chooserButtonWidth, marginTop: 0 }}
                triggerButtonClassName="employment-chooser-action-btn text-decoration-none"
                triggerButtonStyle={{ width: chooserButtonWidth }}
              />
              </div>
              {/*
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none text-start"
                style={{ fontSize: "0.8rem", width: chooserButtonWidth }}
                onClick={() => onSelectDocumentUpload({ precheckOfferLetter: true })}
                disabled={chooserActionsDisabled}
              >
                Newly employed? Click here.
              </button>
              */}
              {validationMessage ? (
                <div
                  className="invalid-feedback bg-danger text-white mb-2 d-inline"
                  style={{
                    display: "block",
                    width: chooserButtonWidth,
                    fontSize: "80%",
                    marginTop: 14,
                    fontWeight: 450,
                  }}
                >
                  {validationMessage}
                </div>
              ) : null}
            </div>
          )}
        </CardContent>
        {showArgyleValidationMessages && (
          <ArgylePayrollValidationMessages
            role={role}
            validationMessages={argyleValidationMessages}
            sourceId={sourceId}
            trnId={trn_id}
          />
        )}
        <Confirm
          isOpen={confirmDeleteOpen}
          sx={{
            "& .RaConfirm-iconPaddingStyle": {
              display: "none",
            },
          }}
          title="Confirm"
          content="Are you sure you want to remove this employer?"
          onConfirm={() => {
            setConfirmDeleteOpen(false);
            onDeleteSource?.();
          }}
          onClose={() => setConfirmDeleteOpen(false)}
        />
      </div>
    </div>
  );
};

export default EmployerVerificationChooser;
